import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Drawer,
  Fab,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Collapse,
  Divider,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  GridView as GridIcon,
  ExpandLess,
  ExpandMore,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../../../context/CategoryContext';
import { useCategoryName } from '../../../utils/categoryTranslation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
const BASE_URL = API_URL.replace('/api/v1', '');

const getCategoryImageUrl = (cat) => {
  const url = cat.imageUrl || cat.image_url || cat.icon || null;
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BASE_URL}${url}`;
};

const DRAWER_WIDTH = 300;

const CategorySidebar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { categoriesList } = useCategories();
  const getCategoryName = useCategoryName();

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});

  const handleFabClick = () => {
    setExpanded({});
    setOpen(true);
  };
  const handleClose = () => {
    setExpanded({});
    setOpen(false);
  };

  const handleParentClick = useCallback((catId) => {
    setExpanded((prev) => ({ ...prev, [catId]: !prev[catId] }));
  }, []);

  const handleChildClick = useCallback(
    (child) => {
      handleClose();
      navigate(`/search?category=${child.id}`);
    },
    [navigate]
  );

  const handleParentNavigate = useCallback(
    (parent) => {
      // If no children, navigate directly
      if (!parent.children || parent.children.length === 0) {
        handleClose();
        navigate(`/search?category=${parent.id}`);
      } else {
        handleParentClick(parent.id);
      }
    },
    [navigate, handleParentClick]
  );

  const activeParents = useMemo(() => {
    const list = Array.isArray(categoriesList) ? categoriesList : [];
    const activeList = list.filter((category) => category.isActive !== false);
    const childrenByParentId = activeList.reduce((acc, category) => {
      if (!category.parentId) return acc;
      if (!acc[category.parentId]) acc[category.parentId] = [];
      acc[category.parentId].push(category);
      return acc;
    }, {});

    const sortCategories = (items) => items.sort((left, right) => {
      const leftOrder = left.order_index ?? left.display_order ?? left.position ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = right.order_index ?? right.display_order ?? right.position ?? Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return String(getCategoryName(left) || left.name || '').localeCompare(String(getCategoryName(right) || right.name || ''));
    });

    return sortCategories(
      activeList
        .filter((category) => !category.parentId)
        .map((parent) => ({
          ...parent,
          children: sortCategories([...(childrenByParentId[parent.id] || [])]),
        }))
    );
  }, [categoriesList, getCategoryName]);

  return (
    <>
      {/* FAB — fixed top-left */}
      <Fab
        onClick={handleFabClick}
        aria-label={t('homepage.categories.fabLabel', 'Parcourir les catégories')}
        sx={{
          position: 'fixed',
          top: { xs: 380, md: 390 },
          left: { xs: 16, md: 24 },
          zIndex: theme.zIndex.fab,
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
          border: `1px solid ${theme.palette.divider}`,
          '&:hover': {
            bgcolor: theme.palette.primary.main,
            color: '#fff',
            borderColor: theme.palette.primary.main,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.5)}`,
          },
          transition: 'background-color 0.2s, color 0.2s',
        }}
      >
        <GridIcon />
      </Fab>

      {/* Drawer */}
      <Drawer
        anchor="left"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: DRAWER_WIDTH,
            bgcolor: 'background.paper',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('homepage.categories.drawerTitle', 'Catégories')}
          </Typography>
          <IconButton size="small" onClick={handleClose} aria-label="fermer">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Category tree */}
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          <List disablePadding>
            {activeParents.map((parent, idx) => {
              const hasChildren = Array.isArray(parent.children) && parent.children.length > 0;
              const isExpanded = !!expanded[parent.id];
              const imageUrl = getCategoryImageUrl(parent);
              const name = getCategoryName(parent) || parent.name;

              return (
                <React.Fragment key={parent.id}>
                  {idx > 0 && <Divider component="li" />}

                  {/* Parent row */}
                  <ListItemButton
                    onClick={() => handleParentNavigate(parent)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                      },
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 60 }}>
                      <Avatar
                        src={imageUrl || undefined}
                        alt={name}
                        variant="rounded"
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          color: theme.palette.primary.main,
                          fontSize: '1.1rem',
                          fontWeight: 700,
                        }}
                      >
                        {!imageUrl && name.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={name}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: 600,
                        color: 'text.primary',
                      }}
                    />

                    {hasChildren ? (
                      isExpanded ? (
                        <ExpandLess sx={{ color: 'text.secondary', fontSize: 20 }} />
                      ) : (
                        <ExpandMore sx={{ color: 'text.secondary', fontSize: 20 }} />
                      )
                    ) : (
                      <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                    )}
                  </ListItemButton>

                  {/* Children collapse */}
                  {hasChildren && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {parent.children
                          .filter((child) => child.isActive !== false)
                          .map((child) => {
                            const childName = getCategoryName(child) || child.name;
                            const childImageUrl = getCategoryImageUrl(child);
                            return (
                              <ListItemButton
                                key={child.id}
                                onClick={() => handleChildClick(child)}
                                sx={{
                                  pl: 4,
                                  pr: 2,
                                  py: 1,
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                                  },
                                }}
                              >
                                <ListItemAvatar sx={{ minWidth: 44 }}>
                                  <Avatar
                                    src={childImageUrl || undefined}
                                    alt={childName}
                                    variant="rounded"
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                                      color: theme.palette.primary.main,
                                      fontSize: '0.8rem',
                                      fontWeight: 700,
                                    }}
                                  >
                                    {!childImageUrl && childName.charAt(0).toUpperCase()}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={childName}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    color: 'text.secondary',
                                  }}
                                />
                                <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: 16 }} />
                              </ListItemButton>
                            );
                          })}
                      </List>
                      <Divider />
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default CategorySidebar;
